/**
 * Builds Maple Grove from the shared world data.
 *
 * The layout comes from `src/sim/data/world.ts`, the same file the simulation
 * uses for travel distances and job placement, so what the player sees and what
 * the economy believes can never drift apart.
 *
 * Everything is procedural geometry. The goal is not photorealism — it is that
 * the place reads instantly: you can tell a house from a shop from the depot at
 * a glance, and you can tell which yards need your services.
 */
import * as THREE from 'three';
import {
  LOCATIONS, ROADS, WORLD_BOUNDS, type WorldLocation,
} from '../../sim/data/world.js';
import {
  standard, grassMaterial, roadMaterial, pavementMaterial, kerbMaterial,
  windowMaterial, lampMaterial, roadMarkingTexture, contactShadowMaterial,
  leafMaterial,
} from './Materials.js';
import { buildBuilding, shade } from './Buildings.js';
import { MeshBatcher } from './MeshBatcher.js';

export interface Collider {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /**
   * How tall the obstruction is, in metres.
   *
   * The camera's occlusion test is a 2D check on the ground plane, which meant
   * a camera lifted above a rooftop still counted as blocked by the building
   * under it - so raising the pitch to frame a yard shoved the camera straight
   * into the house. With a height, the test can ignore anything it is above.
   */
  top: number;
  /**
   * Blocks the camera but not the player.
   *
   * Tree canopies are the case this exists for: brushing past a trunk should
   * cost the player nothing, but a camera that ends up inside a canopy fills
   * the screen with an unlit black polygon and the game looks broken.
   */
  soft?: boolean;
}

export interface BuiltWorld {
  group: THREE.Group;
  colliders: Collider[];
  /** Emissive windows, brightened at night. */
  windowMaterial: THREE.MeshStandardMaterial;
  /** Lamp glass, brightened at night. */
  lampMaterial: THREE.MeshStandardMaterial;
  /** Per-location marker groups so the HUD can flag active jobs. */
  jobMarkers: Map<string, THREE.Group>;
  /** Per-location lawn meshes so mowing can visibly change them. */
  lawns: Map<string, THREE.Mesh>;
  /** Lamp point lights, switched at dusk. */
  lampLights: THREE.PointLight[];
  stats: { triangles: number; objects: number };
}

export function buildNeighbourhood(): BuiltWorld {
  const group = new THREE.Group();
  const colliders: Collider[] = [];
  const jobMarkers = new Map<string, THREE.Group>();
  const lawns = new Map<string, THREE.Mesh>();
  const lampLights: THREE.PointLight[] = [];
  const winMat = windowMaterial();
  const lampMat = lampMaterial();

  // --- ground --------------------------------------------------------------
  const width = WORLD_BOUNDS.maxX - WORLD_BOUNDS.minX + 120;
  const depth = WORLD_BOUNDS.maxZ - WORLD_BOUNDS.minZ + 120;
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), grassMaterial());
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  group.add(ground);

  // --- roads, kerbs, pavements, markings -----------------------------------
  group.add(buildStreets());

  // --- buildings -----------------------------------------------------------
  //
  // Everything static goes through one batcher and is emitted as a single mesh
  // per material. Built as individual objects this scene measured 1,593 draw
  // calls for 39,000 triangles - draw-call bound by an order of magnitude.
  const statics = new MeshBatcher();

  for (const location of LOCATIONS) {
    if (location.height <= 0) {
      const park = buildPark(location);
      statics.addObject(park.group);
      colliders.push(...park.colliders);
      continue;
    }
    const built = buildBuilding(location);
    colliders.push(...built.colliders);
    jobMarkers.set(location.id, built.marker);
    if (built.lawn) lawns.set(location.id, built.lawn);

    // Markers animate and lawns change state, so they stay as live objects;
    // the architecture itself never moves.
    built.group.remove(built.marker);
    if (built.lawn) built.group.remove(built.lawn);
    const holder = new THREE.Group();
    holder.position.copy(built.group.position);
    holder.add(built.marker);
    if (built.lawn) holder.add(built.lawn);
    group.add(holder);

    statics.addObject(built.group);
  }

  // --- trees, lamps, props -------------------------------------------------
  statics.addObject(buildTrees(colliders));
  const lamps = buildStreetLamps(lampMat);
  statics.addObject(lamps.group);
  for (const light of lamps.lights) {
    // Lights cannot be batched; reparent them to the world with their baked
    // position so the geometry can be.
    light.updateWorldMatrix(true, false);
    const world = new THREE.Vector3().setFromMatrixPosition(light.matrixWorld);
    light.position.copy(world);
    group.add(light);
    lampLights.push(light);
  }
  statics.addObject(buildProps());
  statics.addObject(buildParkedCars(colliders));

  // --- world edge ----------------------------------------------------------
  // A treeline reads far better than a wall: it says "the neighbourhood ends
  // here" rather than "the level ends here".
  const edge = buildEdge();
  statics.addObject(edge.group);
  colliders.push(...edge.colliders);

  group.add(statics.build());

  let triangles = 0;
  let objects = 0;
  group.traverse((o) => {
    objects++;
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && mesh.geometry) {
      const index = mesh.geometry.index;
      const position = mesh.geometry.attributes.position;
      triangles += index ? index.count / 3 : (position ? position.count / 3 : 0);
    }
  });

  return {
    group, colliders, windowMaterial: winMat, lampMaterial: lampMat,
    jobMarkers, lawns, lampLights,
    stats: { triangles: Math.round(triangles), objects },
  };
}

/**
 * Streets: tarmac, kerbs that stand proud of the road, paving, centre lines and
 * zebra crossings at the junctions. Previously two flat quads at slightly
 * different heights, which is why the grass/tarmac boundary read as a seam
 * rather than a kerb (VISUAL_AUDIT V-05).
 */
function buildStreets(): THREE.Group {
  const holder = new THREE.Group();
  const kerbMat = kerbMaterial();

  for (const road of ROADS) {
    const horizontal = Math.abs(road.z2 - road.z1) > Math.abs(road.x2 - road.x1) ? false : true;
    const length = Math.hypot(road.x2 - road.x1, road.z2 - road.z1);
    const cx = (road.x1 + road.x2) / 2;
    const cz = (road.z1 + road.z2) / 2;
    const half = road.width / 2;

    // Pavement slab
    const pave = new THREE.Mesh(
      new THREE.PlaneGeometry(
        horizontal ? length : road.width + 8,
        horizontal ? road.width + 8 : length,
      ),
      pavementMaterial(),
    );
    pave.rotation.x = -Math.PI / 2;
    pave.position.set(cx, 0.03, cz);
    pave.receiveShadow = true;
    holder.add(pave);

    // Kerbs — raised strips either side of the carriageway.
    for (const sign of [-1, 1]) {
      const kerb = new THREE.Mesh(
        new THREE.BoxGeometry(
          horizontal ? length : 0.55,
          0.17,
          horizontal ? 0.55 : length,
        ),
        kerbMat,
      );
      kerb.position.set(
        horizontal ? cx : cx + sign * half,
        0.085,
        horizontal ? cz + sign * half : cz,
      );
      // A chamfer along the top edge (VISUAL_REALISM_AUDIT R-06). Every edge in
      // this world met at a mathematically sharp 90 degrees; a real kerb is
      // bevelled, and that bevel catches a highlight that tells the eye how big
      // the thing is and what it is made of. Kerbs get one because the camera
      // passes within a metre of them constantly.
      const chamfer = new THREE.Mesh(
        new THREE.BoxGeometry(
          horizontal ? length : 0.62,
          0.05,
          horizontal ? 0.62 : length,
        ),
        kerbMat,
      );
      chamfer.position.set(kerb.position.x, 0.155, kerb.position.z);
      chamfer.receiveShadow = true;
      holder.add(chamfer);
      kerb.receiveShadow = true;
      kerb.castShadow = true;
      holder.add(kerb);
    }

    // Carriageway, slightly below the kerb top.
    const surface = new THREE.Mesh(
      new THREE.PlaneGeometry(
        horizontal ? length : road.width - 0.6,
        horizontal ? road.width - 0.6 : length,
      ),
      roadMaterial(),
    );
    surface.rotation.x = -Math.PI / 2;
    surface.position.set(cx, 0.05, cz);
    surface.receiveShadow = true;
    holder.add(surface);

    // Dashed centre line.
    const markingTex = roadMarkingTexture('centre').clone();
    markingTex.needsUpdate = true;
    markingTex.repeat.set(horizontal ? length / 12 : 1, horizontal ? 1 : length / 12);
    const line = new THREE.Mesh(
      new THREE.PlaneGeometry(horizontal ? length : 1.4, horizontal ? 1.4 : length),
      new THREE.MeshBasicMaterial({ map: markingTex, transparent: true, depthWrite: false }),
    );
    line.rotation.x = -Math.PI / 2;
    if (horizontal) line.rotation.z = Math.PI / 2;
    line.position.set(cx, 0.062, cz);
    holder.add(line);
  }

  // Zebra crossings where the streets meet.
  const junctions: [number, number][] = [];
  for (const z of [-30, 30]) for (const x of [-60, 0, 60]) junctions.push([x, z]);
  for (const [x, z] of junctions) {
    for (const [ox, oz, rot] of [
      [0, -8.5, 0], [0, 8.5, 0], [-8.5, 0, Math.PI / 2], [8.5, 0, Math.PI / 2],
    ] as [number, number, number][]) {
      const tex = roadMarkingTexture('crossing').clone();
      tex.needsUpdate = true;
      tex.repeat.set(2, 1);
      const zebra = new THREE.Mesh(
        new THREE.PlaneGeometry(8.4, 3.4),
        new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }),
      );
      zebra.rotation.x = -Math.PI / 2;
      zebra.rotation.z = rot;
      zebra.position.set(x + ox, 0.065, z + oz);
      holder.add(zebra);
    }

    // Drain covers, because tiny irregularities are what stop tarmac reading
    // as a texture swatch.
    for (const [dx, dz] of [[-4.6, -4.6], [4.6, 4.6]] as [number, number][]) {
      const drain = new THREE.Mesh(
        new THREE.BoxGeometry(0.7, 0.04, 0.5),
        standard(0x54585e, { rough: 0.7, metal: 0.35 }),
      );
      drain.position.set(x + dx, 0.07, z + dz);
      holder.add(drain);
    }
  }

  return holder;
}

function buildPark(location: WorldLocation): { group: THREE.Object3D; colliders: Collider[] } {
  const holder = new THREE.Group();
  holder.position.set(location.x, 0, location.z);
  const colliders: Collider[] = [];

  // Uses the same grass material as the surrounding ground, so the park does
  // not read as a green rectangle pasted onto a different green.
  const lawn = new THREE.Mesh(
    new THREE.PlaneGeometry(location.width, location.depth),
    grassMaterial(),
  );
  lawn.rotation.x = -Math.PI / 2;
  lawn.position.y = 0.062;
  lawn.receiveShadow = true;
  holder.add(lawn);

  for (const rotated of [false, true]) {
    const path = new THREE.Mesh(
      new THREE.PlaneGeometry(rotated ? 4.4 : location.width, rotated ? location.depth : 4.4),
      pavementMaterial(),
    );
    path.rotation.x = -Math.PI / 2;
    path.position.y = 0.08;
    path.receiveShadow = true;
    holder.add(path);
  }

  const plaza = new THREE.Mesh(new THREE.CircleGeometry(7, 28), pavementMaterial());
  plaza.rotation.x = -Math.PI / 2;
  plaza.position.y = 0.1;
  plaza.receiveShadow = true;
  holder.add(plaza);

  // Fountain
  const basin = new THREE.Mesh(
    new THREE.CylinderGeometry(3.1, 3.3, 0.8, 24),
    standard(0x9d9a92, { rough: 0.85 }),
  );
  basin.position.y = 0.4;
  basin.castShadow = true;
  basin.receiveShadow = true;
  holder.add(basin);

  // The fountain is solid: the player used to walk through it and the camera
  // used to sit inside the bowl.
  colliders.push({
    minX: location.x - 3.3, maxX: location.x + 3.3,
    minZ: location.z - 3.3, maxZ: location.z + 3.3, top: 3.6,
  });

  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(3.15, 0.16, 8, 28),
    standard(0xb3b0a8, { rough: 0.8 }),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.8;
  holder.add(rim);

  const water = new THREE.Mesh(
    new THREE.CylinderGeometry(2.85, 2.85, 0.1, 24),
    // No environment map in the scene, so metalness has nothing to reflect and
    // only makes the surface darker. A bright low-roughness dielectric with a
    // little emissive reads as water far better here.
    new THREE.MeshStandardMaterial({
      color: 0x4d9fc4, roughness: 0.15, metalness: 0,
      emissive: 0x1d4a63, emissiveIntensity: 0.35,
    }),
  );
  water.position.y = 0.74;
  holder.add(water);

  const plinth = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.8, 2.6, 12),
    standard(0xa8a49c, { rough: 0.85 }),
  );
  plinth.position.y = 1.7;
  plinth.castShadow = true;
  holder.add(plinth);

  const bowl = new THREE.Mesh(
    new THREE.CylinderGeometry(1.3, 0.5, 0.5, 14),
    standard(0xb3b0a8, { rough: 0.8 }),
  );
  bowl.position.y = 3.1;
  bowl.castShadow = true;
  holder.add(bowl);

  for (const [px, pz, rot] of [
    [0, 10, 0], [0, -10, Math.PI],
    [10, 0, -Math.PI / 2], [-10, 0, Math.PI / 2],
  ] as [number, number, number][]) {
    const bench = buildBench();
    bench.position.set(px, 0, pz);
    bench.rotation.y = rot;
    holder.add(bench);
  }

  // Shade trees on the quarters, so the park has a canopy and something for the
  // eye to travel past. A lawn with a fountain in it and nothing else reads as
  // a roundabout, not a green.
  {
    let treeSeed = 4477;
    const treeRand = () => {
      treeSeed = (treeSeed * 1103515245 + 12345) & 0x7fffffff;
      return treeSeed / 0x7fffffff;
    };
    const spots: [number, number][] = [];
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        spots.push([sx * (location.width * 0.19 + treeRand() * 4), sz * (location.depth * 0.2 + treeRand() * 4)]);
        spots.push([sx * (location.width * 0.34 + treeRand() * 3), sz * (location.depth * 0.09 + treeRand() * 3)]);
      }
    }
    for (const [tx, tz] of spots) {
      const tree = buildBroadleaf(0.85 + treeRand() * 0.5, treeRand);
      tree.position.set(tx, 0, tz);
      tree.rotation.y = treeRand() * Math.PI;
      holder.add(tree);
      colliders.push({
        minX: location.x + tx - 1.7, maxX: location.x + tx + 1.7,
        minZ: location.z + tz - 1.7, maxZ: location.z + tz + 1.7,
        top: 8, soft: true,
      });
    }

    // Litter bins and a noticeboard: the small municipal things that say a
    // council maintains this place.
    for (const [bx, bz] of [[6.4, 6.4], [-6.4, -6.4]] as [number, number][]) {
      const bin = new THREE.Mesh(
        new THREE.CylinderGeometry(0.34, 0.3, 0.86, 10),
        standard(0x35473a, { rough: 0.8, metal: 0.2 }),
      );
      bin.position.set(bx, 0.43, bz);
      bin.castShadow = true;
      holder.add(bin);
      const lip = new THREE.Mesh(
        new THREE.TorusGeometry(0.35, 0.05, 6, 12),
        standard(0x22302a, { rough: 0.8 }),
      );
      lip.rotation.x = Math.PI / 2;
      lip.position.set(bx, 0.88, bz);
      holder.add(lip);
    }
    for (const sign of [-1, 1]) {
      const postMat = standard(0x6b5842, { rough: 1 });
      const board = new THREE.Group();
      for (const px of [-0.7, 0.7]) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.13, 1.6, 0.13), postMat);
        post.position.set(px, 0.8, 0);
        post.castShadow = true;
        board.add(post);
      }
      const panel = new THREE.Mesh(
        new THREE.BoxGeometry(1.5, 0.9, 0.1),
        standard(0x4a5a44, { rough: 0.85 }),
      );
      panel.position.y = 1.5;
      panel.castShadow = true;
      board.add(panel);
      // Notices pinned to it. Without them the board is a black slab that reads
      // as a hole in the world from any distance.
      const paperMat = standard(0xe8e4d6, { rough: 0.95 });
      for (const [nx, ny, nw, nh] of [
        [-0.42, 0.12, 0.42, 0.5], [0.16, 0.2, 0.34, 0.42], [0.44, -0.16, 0.3, 0.26],
      ] as [number, number, number, number][]) {
        const note = new THREE.Mesh(new THREE.BoxGeometry(nw, nh, 0.03), paperMat);
        note.position.set(nx, 1.5 + ny, 0.06);
        board.add(note);
      }
      board.position.set(sign * 7.2, 0, sign * 7.8);
      board.rotation.y = sign > 0 ? 0.4 : Math.PI + 0.4;
      holder.add(board);
    }
  }

  // Planting beds around the edge. Varied in size, tone and spacing, because a
  // row of identically sized blobs at even intervals is the single most
  // obviously procedural thing a scene can contain.
  const shrubMats = [
    standard(0x3f7233, { rough: 1 }),
    standard(0x4d8438, { rough: 1 }),
    standard(0x36622c, { rough: 1 }),
    standard(0x5a8f42, { rough: 1 }),
  ];
  const flowerMats = [
    standard(0xc4506a, { rough: 0.9 }),
    standard(0xd8a03a, { rough: 0.9 }),
    standard(0x9a6bc4, { rough: 0.9 }),
    standard(0xe8e2d0, { rough: 0.9 }),
  ];
  let plantSeed = 5150;
  const plantRand = () => {
    plantSeed = (plantSeed * 1103515245 + 12345) & 0x7fffffff;
    return plantSeed / 0x7fffffff;
  };
  for (const [hx, hz, along] of [
    [0, location.depth / 2 - 2.4, 'x'],
    [0, -location.depth / 2 + 2.4, 'x'],
    [location.width / 2 - 2.4, 0, 'z'],
    [-location.width / 2 + 2.4, 0, 'z'],
  ] as [number, number, 'x' | 'z'][]) {
    const span = (along === 'x' ? location.width : location.depth) - 13;
    // Walk the bed with uneven gaps rather than stepping a fixed interval.
    let t = -span / 2;
    let guard = 0;
    while (t < span / 2 && guard++ < 60) {
      const jitter = (plantRand() - 0.5) * 1.4;
      const x = hx + (along === 'x' ? t : jitter);
      const z = hz + (along === 'z' ? t : jitter);
      const r = 0.34 + plantRand() * 0.42;
      // The occasional gap, as a real bed has.
      if (plantRand() < 0.13) { t += 2.2 + plantRand() * 1.6; continue; }

      const shrub = buildBush(r, plantRand, shrubMats);
      shrub.position.set(x, 0, z);
      shrub.rotation.y = plantRand() * Math.PI;
      holder.add(shrub);

      // Flowers sit inside the shrub mass, not perched on top of it.
      if (plantRand() < 0.34) {
        const petals = 2 + Math.floor(plantRand() * 3);
        const mat = flowerMats[Math.floor(plantRand() * flowerMats.length)]!;
        for (let f = 0; f < petals; f++) {
          const flower = new THREE.Mesh(new THREE.IcosahedronGeometry(r * 0.2, 0), mat);
          flower.position.set(
            x + (plantRand() - 0.5) * r * 1.5,
            r * (0.55 + plantRand() * 0.5),
            z + (plantRand() - 0.5) * r * 1.5,
          );
          holder.add(flower);
        }
      }
      t += r * 1.7 + 0.5 + plantRand() * 0.9;
    }
  }


  return { group: holder, colliders };
}

/**
 * A park bench at human scale.
 *
 * The previous one had a 0.5 m seat and a 2.7 m span, which put the seat at the
 * player's waist and made it read as set dressing from a different game. A real
 * bench seats at about 0.44 m and spans 1.8 m.
 */
/**
 * A shrub built from overlapping faceted lobes.
 *
 * One smooth sphere reads as a green potato; two or three low-detail lobes at
 * different heights and tones read as foliage. Detail 0 is deliberate - the
 * visible facets match the rest of the art style, where a subdivided sphere
 * looks like a placeholder from a different game.
 */
function buildBush(
  radius: number,
  rand: () => number,
  mats: THREE.MeshStandardMaterial[],
): THREE.Group {
  const bush = new THREE.Group();
  const lobes = 2 + Math.floor(rand() * 3);
  const tint = new THREE.Color(
    (mats[Math.floor(rand() * mats.length)]!).color.getHex(),
  );
  for (let i = 0; i < lobes; i++) {
    const lobeRadius = radius * (i === 0 ? 1 : 0.55 + rand() * 0.4);
    // The same leaf cards the trees use, at shrub scale. A hedge seen from two
    // metres away is where a smooth convex shell is least forgivable.
    const lobe = buildCanopy(lobeRadius * 1.15, rand, tint, 5);
    const spread = i === 0 ? 0 : radius * (0.5 + rand() * 0.6);
    const angle = rand() * Math.PI * 2;
    lobe.position.set(
      Math.cos(angle) * spread,
      lobeRadius * (i === 0 ? 0.72 : 0.55 + rand() * 0.5),
      Math.sin(angle) * spread,
    );
    lobe.scale.set(1, 0.78 + rand() * 0.3, 1);
    bush.add(lobe);
  }
  return bush;
}

/**
 * Cars parked along the kerbs.
 *
 * Empty carriageways are the loudest thing in a screenshot of a town: real
 * streets are lined with other people's property. These are static - ADR-0008
 * still stands on driving - but they fill the road, give scale to the buildings
 * and put something in the frame between the player and the far pavement.
 */
function buildParkedCars(colliders: Collider[]): THREE.Object3D {
  const holder = new THREE.Group();
  const bodyColours = [
    0x9aa3ad, 0x2f3a4a, 0xb04a3c, 0x33484a, 0xd8d2c6,
    0x2b2f36, 0x5a6b7d, 0x7d5a3c, 0xc9b06a, 0x44506b,
  ];
  let seed = 20260828;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };

  for (const road of ROADS) {
    const horizontal = Math.abs(road.x2 - road.x1) >= Math.abs(road.z2 - road.z1);
    const length = Math.hypot(road.x2 - road.x1, road.z2 - road.z1);
    const from = horizontal ? Math.min(road.x1, road.x2) : Math.min(road.z1, road.z2);
    // Park against the kerb, not in the running lane.
    const offset = road.width / 2 - 1.7;

    let t = 8 + rand() * 14;
    let guard = 0;
    while (t < length - 10 && guard++ < 40) {
      // Long gaps: a kerb parked solid end to end reads as a car park.
      if (rand() < 0.55) { t += 9 + rand() * 22; continue; }
      const along = from + t;
      const side = rand() < 0.5 ? -1 : 1;
      const x = horizontal ? along : road.x1 + side * offset;
      const z = horizontal ? road.z1 + side * offset : along;

      // Junctions and crossings must stay clear, or the town looks illegal.
      const nearJunction =
        [-60, 0, 60].some((jx) => Math.abs(x - jx) < 12) &&
        [-30, 30].some((jz) => Math.abs(z - jz) < 12);
      if (nearJunction) { t += 10; continue; }

      const car = buildParkedCar(
        bodyColours[Math.floor(rand() * bodyColours.length)]!,
        rand() < 0.28,
        rand,
      );
      car.position.set(x, 0, z);
      // Face along the road, in the direction of that side's traffic.
      car.rotation.y = horizontal ? (side > 0 ? 0 : Math.PI) : (side > 0 ? Math.PI / 2 : -Math.PI / 2);
      holder.add(car);
      colliders.push({
        minX: x - 1.4, maxX: x + 1.4,
        minZ: z - 1.4, maxZ: z + 1.4,
        top: 1.6,
      });
      t += 7.5 + rand() * 16;
    }
  }
  return holder;
}

/** One car: body, cabin, glass, wheels and lights. Vans get a taller box. */
function buildParkedCar(colour: number, van: boolean, rand: () => number): THREE.Group {
  const car = new THREE.Group();
  const bodyMat = standard(colour, { rough: 0.45, metal: 0.25 });
  const glassMat = standard(0x1e2833, { rough: 0.25, metal: 0.55 });
  const tyreMat = standard(0x16181c, { rough: 1 });
  const length = van ? 5.2 : 4.3 + rand() * 0.5;

  const body = new THREE.Mesh(new THREE.BoxGeometry(length, 0.86, 1.86), bodyMat);
  body.position.y = 0.92;
  body.castShadow = true;
  body.receiveShadow = true;
  car.add(body);

  const cabinLength = van ? length - 1.5 : length * 0.52;
  const cabin = new THREE.Mesh(
    new THREE.BoxGeometry(cabinLength, van ? 1.15 : 0.78, 1.7),
    bodyMat,
  );
  cabin.position.set(van ? 0.5 : -0.25, van ? 1.92 : 1.7, 0);
  cabin.castShadow = true;
  car.add(cabin);

  // Glazing as a slightly inset band, which is what makes a box read as a car.
  const glass = new THREE.Mesh(
    new THREE.BoxGeometry(cabinLength - 0.28, van ? 0.62 : 0.46, 1.74),
    glassMat,
  );
  glass.position.set(cabin.position.x, cabin.position.y + 0.06, 0);
  car.add(glass);

  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.24, 10), tyreMat);
      wheel.rotation.x = Math.PI / 2;
      wheel.position.set(sx * (length / 2 - 1.05), 0.34, sz * 0.92);
      wheel.castShadow = true;
      car.add(wheel);
    }
  }

  const lampMats = [standard(0xf6efd8, { rough: 0.3 }), standard(0x8c2b22, { rough: 0.4 })];
  for (const [sx, mat] of [[1, lampMats[0]!], [-1, lampMats[1]!]] as [number, THREE.Material][]) {
    for (const sz of [-1, 1]) {
      const lamp = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.22, 0.42), mat);
      lamp.position.set(sx * (length / 2 + 0.01), 1.0, sz * 0.6);
      car.add(lamp);
    }
  }
  return car;
}

function buildBench(): THREE.Group {
  const bench = new THREE.Group();
  const wood = standard(0x8a6440, { rough: 0.92 });
  const metal = standard(0x33373d, { rough: 0.6, metal: 0.5 });

  for (let i = 0; i < 3; i++) {
    const slat = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.055, 0.14), wood);
    slat.position.set(0, 0.44, -0.17 + i * 0.17);
    slat.castShadow = true;
    bench.add(slat);
  }
  for (let i = 0; i < 3; i++) {
    const slat = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.13, 0.05), wood);
    slat.position.set(0, 0.6 + i * 0.16, -0.25);
    slat.rotation.x = -0.14;
    slat.castShadow = true;
    bench.add(slat);
  }
  for (const x of [-0.76, 0.76]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.44, 0.44), metal);
    leg.position.set(x, 0.22, -0.02);
    leg.castShadow = true;
    bench.add(leg);
    const upright = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.52, 0.055), metal);
    upright.position.set(x, 0.68, -0.26);
    upright.rotation.x = -0.14;
    bench.add(upright);
  }
  bench.add(contactShadow(2.1, 0.9));
  return bench;
}

/**
 * Trees with variation. Three species, randomised height, tilt, canopy layering
 * and colour — previously one cylinder plus one icosahedron, twenty times
 * (VISUAL_AUDIT V-09).
 */
/** A single broadleaf, shared by the park planting and anywhere else one is wanted. */
/**
 * A canopy built from alpha-tested leaf cards.
 *
 * Cards are placed on a squashed sphere and turned to face outward, so the
 * silhouette against the sky is ragged the way a real canopy is rather than a
 * smooth convex shell. A small solid core stays inside: from across the
 * neighbourhood a tree still needs to read as a mass and cast a coherent
 * shadow, and cards alone go transparent at grazing angles.
 *
 * Every card shares one material (see `leafMaterial`), so the whole
 * neighbourhood's foliage merges into a single draw call through MeshBatcher.
 * The per-card tint rides in vertex colours instead.
 */
function buildCanopy(
  radius: number,
  rand: () => number,
  tint: THREE.Color,
  cards = 9,
): THREE.Group {
  const canopy = new THREE.Group();

  // Inner mass, well inside the cards so it never pokes through.
  const core = new THREE.Mesh(
    new THREE.IcosahedronGeometry(radius * 0.62, 0),
    standard(tint.getHex(), { rough: 1 }),
  );
  // The core is the canopy's shadow caster. The cards are not: eight
  // double-sided alpha-tested casters per tree self-shadow into near-black, and
  // cost a shaped depth render each. One coherent blob shadow from the core is
  // both cheaper and what a canopy actually casts.
  core.castShadow = true;
  canopy.add(core);

  const leaf = leafMaterial();
  for (let i = 0; i < cards; i++) {
    // Fibonacci-ish distribution: evenly spread without a visible grid.
    const t = (i + 0.5) / cards;
    const phi = Math.acos(1 - 2 * t);
    const theta = Math.PI * (1 + Math.sqrt(5)) * i;
    const dir = new THREE.Vector3(
      Math.sin(phi) * Math.cos(theta),
      Math.cos(phi) * 0.8,
      Math.sin(phi) * Math.sin(theta),
    );

    const size = radius * (1.25 + rand() * 0.5);
    const geometry = new THREE.PlaneGeometry(size, size);

    // Darker underneath, lighter on top: the single strongest cue that a canopy
    // has volume rather than being a flat green shape. Centred slightly above
    // 1 so the tint the palette asked for is what the top of the tree shows.
    const shade = 0.82 + Math.max(0, dir.y) * 0.55 + rand() * 0.14;
    const colour = tint.clone().multiplyScalar(shade);
    const count = geometry.attributes.position!.count;
    const colours = new Float32Array(count * 3);
    for (let v = 0; v < count; v++) {
      colours[v * 3] = colour.r;
      colours[v * 3 + 1] = colour.g;
      colours[v * 3 + 2] = colour.b;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));

    const card = new THREE.Mesh(geometry, leaf);
    card.position.copy(dir).multiplyScalar(radius * 0.58);
    card.lookAt(dir.clone().multiplyScalar(radius * 4));
    card.rotateZ(rand() * Math.PI);
    canopy.add(card);
  }
  return canopy;
}

function buildBroadleaf(scale: number, rand: () => number): THREE.Group {
  const tree = new THREE.Group();
  const trunkHeight = 3.4 * scale;
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.28 * scale, 0.42 * scale, trunkHeight, 7),
    standard(0x5b4433, { rough: 1 }),
  );
  trunk.position.y = trunkHeight / 2;
  trunk.castShadow = true;
  tree.add(trunk);
  const tints = [0x3f7a35, 0x4c8a3a, 0x35682f];
  const tint = new THREE.Color(tints[Math.floor(rand() * tints.length)]!);
  for (let i = 0; i < 2; i++) {
    const canopy = buildCanopy((2.1 - i * 0.45) * scale, rand, tint, 8);
    canopy.position.set(
      (rand() - 0.5) * 1.2 * scale,
      trunkHeight + 1 * scale + i * 1.3 * scale,
      (rand() - 0.5) * 1.2 * scale,
    );
    tree.add(canopy);
  }
  return tree;
}

function buildTrees(colliders: Collider[]): THREE.Object3D {
  const holder = new THREE.Group();
  const trunkMat = standard(0x5b4433, { rough: 1 });
  const trunkMatPale = standard(0x8a7d68, { rough: 1 });
  const leafTints = [0x3f7a35, 0x4c8a3a, 0x35682f, 0x5c8f3a];

  let seed = 12345;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };

  const spots: [number, number][] = [];
  for (const z of [-30, 30]) {
    for (let x = -86; x <= 86; x += 13) spots.push([x, z + (Math.round(x / 13) % 2 === 0 ? 9.5 : -9.5)]);
  }
  for (const x of [-60, 60]) {
    for (let z = -86; z <= 86; z += 15) spots.push([x + (Math.round(z / 15) % 2 === 0 ? 9.5 : -9.5), z]);
  }
  for (let i = 0; i < 22; i++) {
    spots.push([rand() * 180 - 90, rand() * 180 - 90]);
  }

  for (const [x, z] of spots) {
    if (LOCATIONS.some((l) =>
      Math.abs(l.x - x) < l.width / 2 + 7 && Math.abs(l.z - z) < l.depth / 2 + 7)) continue;
    // Keep them off the carriageway.
    if (ROADS.some((r) => {
      const horizontal = Math.abs(r.x2 - r.x1) > Math.abs(r.z2 - r.z1);
      return horizontal
        ? Math.abs(z - r.z1) < r.width / 2 + 1 && x > Math.min(r.x1, r.x2) && x < Math.max(r.x1, r.x2)
        : Math.abs(x - r.x1) < r.width / 2 + 1 && z > Math.min(r.z1, r.z2) && z < Math.max(r.z1, r.z2);
    })) continue;

    const species = rand();
    const scale = 0.75 + rand() * 0.7;
    const tree = new THREE.Group();

    if (species < 0.45) {
      // Broadleaf: stacked spheres, offset, for a lumpy natural canopy.
      const trunkHeight = 3.4 * scale;
      const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.28 * scale, 0.42 * scale, trunkHeight, 7),
        trunkMat,
      );
      trunk.position.y = trunkHeight / 2;
      trunk.castShadow = true;
      tree.add(trunk);
      const tint = new THREE.Color(leafTints[Math.floor(rand() * leafTints.length)]!);
      for (let i = 0; i < 2; i++) {
        const canopy = buildCanopy((2.1 - i * 0.45) * scale, rand, tint, 8);
        canopy.position.set(
          (rand() - 0.5) * 1.2 * scale,
          trunkHeight + 1 * scale + i * 1.3 * scale,
          (rand() - 0.5) * 1.2 * scale,
        );
        tree.add(canopy);
      }
    } else if (species < 0.78) {
      // Conifer: stacked cones.
      const trunkHeight = 1.8 * scale;
      const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.22 * scale, 0.34 * scale, trunkHeight, 6),
        trunkMat,
      );
      trunk.position.y = trunkHeight / 2;
      trunk.castShadow = true;
      tree.add(trunk);
      // A conifer's silhouette really is close to a stack of cones, so this one
      // keeps its geometry and gains the tonal range instead: each tier a shade
      // darker toward the base, where less light reaches.
      const base = new THREE.Color(leafTints[Math.floor(rand() * leafTints.length)]!);
      for (let i = 0; i < 3; i++) {
        const tier = base.clone().multiplyScalar(0.72 + i * 0.14);
        const cone = new THREE.Mesh(
          new THREE.ConeGeometry((2.4 - i * 0.6) * scale, 2.6 * scale, 7),
          standard(tier.getHex(), { rough: 1 }),
        );
        cone.position.y = trunkHeight + i * 1.5 * scale + 1.1 * scale;
        cone.castShadow = true;
        tree.add(cone);
      }
    } else {
      // Slim ornamental: pale trunk, tall narrow canopy.
      const trunkHeight = 4.2 * scale;
      const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.18 * scale, 0.26 * scale, trunkHeight, 6),
        trunkMatPale,
      );
      trunk.position.y = trunkHeight / 2;
      trunk.castShadow = true;
      tree.add(trunk);
      const tint = new THREE.Color(leafTints[Math.floor(rand() * leafTints.length)]!);
      const canopy = buildCanopy(1.5 * scale, rand, tint, 7);
      canopy.scale.set(0.8, 1.35, 0.8);
      canopy.position.y = trunkHeight + 1.7 * scale;
      tree.add(canopy);
    }

    tree.position.set(x, 0, z);
    tree.rotation.y = rand() * Math.PI * 2;
    // A slight tilt is the difference between "planted" and "instanced".
    tree.rotation.z = (rand() - 0.5) * 0.06;
    tree.add(contactShadow(3.6 * scale, 3.6 * scale));
    holder.add(tree);
    // Soft: the camera must never end up inside a canopy, but the player is
    // welcome to walk under one.
    const spread = 2.2 * scale;
    colliders.push({
      minX: x - spread, maxX: x + spread,
      minZ: z - spread, maxZ: z + spread,
      top: 9 * scale, soft: true,
    });
  }
  return holder;
}

/**
 * Street lamps that actually light the street. They used to be emissive spheres
 * casting nothing, so nights were uniformly dark (VISUAL_AUDIT V-13).
 */
function buildStreetLamps(lampMat: THREE.MeshStandardMaterial): {
  group: THREE.Group; lights: THREE.PointLight[];
} {
  const holder = new THREE.Group();
  const lights: THREE.PointLight[] = [];
  const poleMat = standard(0x2f3338, { rough: 0.55, metal: 0.5 });

  const place = (x: number, z: number, rotation: number) => {
    const lamp = new THREE.Group();
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.36, 0.35, 8), poleMat);
    base.position.y = 0.17;
    base.castShadow = true;
    lamp.add(base);

    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.15, 5.4, 8), poleMat);
    pole.position.y = 2.9;
    pole.castShadow = true;
    lamp.add(pole);

    // Curved arm from two segments, which reads far better than a straight bar.
    const arm1 = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.9, 6), poleMat);
    arm1.position.set(0.22, 5.6, 0);
    arm1.rotation.z = -0.9;
    lamp.add(arm1);
    const arm2 = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.85, 6), poleMat);
    arm2.position.set(0.85, 5.82, 0);
    arm2.rotation.z = Math.PI / 2;
    lamp.add(arm2);

    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.42, 0.34, 10), poleMat);
    hood.position.set(1.24, 5.78, 0);
    lamp.add(hood);
    const glass = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), lampMat);
    glass.position.set(1.24, 5.52, 0);
    lamp.add(glass);

    const light = new THREE.PointLight(0xffd9a0, 0, 26, 1.4);
    light.position.set(1.24, 5.4, 0);
    lamp.add(light);
    lights.push(light);

    lamp.position.set(x, 0, z);
    lamp.rotation.y = rotation;
    lamp.add(contactShadow(1.4, 1.4));
    holder.add(lamp);
  };

  for (const z of [-30, 30]) {
    for (let x = -78; x <= 78; x += 26) {
      place(x, z + 7.2, z > 0 ? Math.PI : 0);
    }
  }
  for (const x of [-60, 0, 60]) {
    for (let z = -78; z <= 78; z += 28) {
      if (Math.abs(z) < 22) continue;
      place(x + 7.2, z, x >= 0 ? -Math.PI / 2 : Math.PI / 2);
    }
  }
  return { group: holder, lights };
}

/** Small street furniture. Cheap, and clutter is what makes a place lived in. */
function buildProps(): THREE.Object3D {
  const holder = new THREE.Group();
  let seed = 99117;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };

  const binBody = standard(0x3f5a4a, { rough: 0.8 });
  const binLid = standard(0x2f4438, { rough: 0.7 });
  const hydrantMat = standard(0xb5372c, { rough: 0.6 });
  const boxMat = standard(0x6a7078, { rough: 0.7, metal: 0.3 });

  const spots: [number, number, string][] = [
    [-52, 24, 'bin'], [12, 24, 'bin'], [52, -24, 'bin'], [-14, -24, 'bin'],
    [-66, 36, 'hydrant'], [66, -36, 'hydrant'], [6, 36, 'hydrant'],
    [-46, -24, 'box'], [46, 24, 'box'],
    [-70, 24, 'bin'], [70, 36, 'bin'],
  ];

  for (const [x, z, kind] of spots) {
    const prop = new THREE.Group();
    if (kind === 'bin') {
      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.36, 1.0, 10), binBody);
      body.position.y = 0.5;
      body.castShadow = true;
      const lid = new THREE.Mesh(new THREE.CylinderGeometry(0.46, 0.46, 0.12, 10), binLid);
      lid.position.y = 1.05;
      lid.castShadow = true;
      prop.add(body, lid, contactShadow(1.3, 1.3));
    } else if (kind === 'hydrant') {
      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.2, 0.7, 8), hydrantMat);
      body.position.y = 0.35;
      body.castShadow = true;
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.19, 8, 6), hydrantMat);
      cap.position.y = 0.74;
      const arms = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.14, 0.14), hydrantMat);
      arms.position.y = 0.52;
      prop.add(body, cap, arms, contactShadow(0.8, 0.8));
    } else {
      const body = new THREE.Mesh(new THREE.BoxGeometry(0.7, 1.25, 0.42), boxMat);
      body.position.y = 0.62;
      body.castShadow = true;
      prop.add(body, contactShadow(1.1, 0.9));
    }
    prop.position.set(x, 0, z);
    prop.rotation.y = rand() * Math.PI * 2;
    holder.add(prop);
  }
  return holder;
}

/**
 * The world edge. A dense treeline rather than a wall: it tells the player the
 * neighbourhood is the whole game for now, instead of that the level is broken.
 */
function buildEdge(): { group: THREE.Group; colliders: Collider[] } {
  const holder = new THREE.Group();
  const colliders: Collider[] = [];
  const { minX, maxX, minZ, maxZ } = WORLD_BOUNDS;
  const trunkMat = standard(0x4a3a2c, { rough: 1 });
  const leafMat = standard(0x2f5f2b, { rough: 1 });

  let seed = 4242;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };

  const line = (from: THREE.Vector2, to: THREE.Vector2) => {
    const length = from.distanceTo(to);
    const count = Math.round(length / 7);
    for (let i = 0; i <= count; i++) {
      const t = i / count;
      const x = from.x + (to.x - from.x) * t + (rand() - 0.5) * 3;
      const z = from.y + (to.y - from.y) * t + (rand() - 0.5) * 3;
      const scale = 1.1 + rand() * 0.7;
      const tree = new THREE.Group();
      const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.3 * scale, 0.42 * scale, 3 * scale, 6),
        trunkMat,
      );
      trunk.position.y = 1.5 * scale;
      tree.add(trunk);
      for (let j = 0; j < 2; j++) {
        const blob = new THREE.Mesh(
          new THREE.IcosahedronGeometry((2.6 - j * 0.5) * scale, 0),
          leafMat,
        );
        blob.position.y = (3.4 + j * 1.5) * scale;
        blob.castShadow = true;
        tree.add(blob);
      }
      tree.position.set(x, 0, z);
      holder.add(tree);
    }
  };

  line(new THREE.Vector2(minX - 4, minZ - 4), new THREE.Vector2(maxX + 4, minZ - 4));
  line(new THREE.Vector2(minX - 4, maxZ + 4), new THREE.Vector2(maxX + 4, maxZ + 4));
  line(new THREE.Vector2(minX - 4, minZ - 4), new THREE.Vector2(minX - 4, maxZ + 4));
  line(new THREE.Vector2(maxX + 4, minZ - 4), new THREE.Vector2(maxX + 4, maxZ + 4));

  // Invisible barriers keep the player inside without a visible wall.
  const thickness = 4;
  for (const [x, z, w, d] of [
    [(minX + maxX) / 2, minZ, maxX - minX + 40, thickness],
    [(minX + maxX) / 2, maxZ, maxX - minX + 40, thickness],
    [minX, (minZ + maxZ) / 2, thickness, maxZ - minZ],
    [maxX, (minZ + maxZ) / 2, thickness, maxZ - minZ],
  ] as [number, number, number, number][]) {
    colliders.push({
      minX: x - w / 2, maxX: x + w / 2, minZ: z - d / 2, maxZ: z + d / 2, top: 40,
    });
  }

  return { group: holder, colliders };
}

/**
 * A soft blob laid on the ground under a prop.
 *
 * The shadow map cannot resolve the small darkening where an object meets the
 * ground at this scale, and without it everything looks like it is hovering —
 * which was the single most "unfinished" tell in the audit (V-04).
 */
export function contactShadow(width: number, depth: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(width, depth),
    contactShadowMaterial(),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.035;
  mesh.renderOrder = 1;
  return mesh;
}

export { shade };
